// -----------------------------------------------------------------------------
// SMSPanel  (Wave B — minimal)
//
// Action panel body for SMS_SENT.
// Minimal: text field + char counter + send button.
// NOTE: no dedicated SMS composer exists in this codebase. This panel posts
// directly to the notes API with contacted_via='TEXT' as a workaround until
// a full SMS composer is built. Marked as minimal in the Task #16 report.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
    Stack,
} from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

const SMS_CHAR_LIMIT = 160;

export default function SMSPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onComplete,
    onClose,
}) {
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const phoneNumber = leadDetail?.lead_contact?.phone || leadDetail?.phone || '';
    const toName = [leadDetail?.first_name, leadDetail?.last_name].filter(Boolean).join(' ') || 'Patient';
    const charsLeft = SMS_CHAR_LIMIT - message.length;

    const handleSend = async () => {
        if (!message.trim()) return;
        setSubmitting(true);
        try {
            const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                'post',
                {
                    lead: leadId,
                    // LeadNote API stores free text in `additional_notes` (`description` is ignored).
                    additional_notes: message.trim(),
                    contacted_via: 'TEXT',
                    ...(treatmentId ? { treatment: treatmentId } : {}),
                }
            );
            if (res.status === 201) {
                dispatch(updateError({ success: true, message: 'SMS logged' }));
                onComplete?.(item);
            } else {
                const msg = res?.data?.detail || 'Failed to send SMS';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="sms-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                height: '100%',
                overflowY: 'auto',
            }}
        >
            {/* Icon header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(0, 184, 217, 0.10)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="mdi:message-text-outline" width={18} height={18} sx={{ color: '#00B8D9' }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                        Send SMS
                    </Typography>
                    {phoneNumber ? (
                        <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                            {toName} · {phoneNumber}
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: '12px', color: '#FF5630', letterSpacing: '-0.005em' }}>
                            No phone number on file
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Message field */}
            <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                        Message
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: charsLeft < 20 ? '#FF5630' : '#6e6e73' }}>
                        {charsLeft} chars left
                    </Typography>
                </Box>
                <TextField
                    data-testid="sms-message"
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    placeholder="Type your SMS…"
                    value={message}
                    onChange={(e) => {
                        if (e.target.value.length <= SMS_CHAR_LIMIT) setMessage(e.target.value);
                    }}
                    autoFocus
                />
            </Box>

            {/* Actions */}
            <Stack direction="row" spacing={1}>
                <Button
                    data-testid="sms-submit"
                    variant="contained"
                    disableElevation
                    onClick={handleSend}
                    disabled={submitting || !message.trim() || !phoneNumber}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:send" width={14} height={14} />}
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: '#00B8D9',
                        '&:hover': { bgcolor: '#0099b8' },
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Sending…' : 'Send SMS'}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                >
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
}

SMSPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

SMSPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
};
